import React from 'react';
import { Hidden } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Form, Formik, FormikProps } from 'formik';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { DeclineMedicalDentalWizard } from './wizard/decline-medical-dental-wizard';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { DeclineMedicalDentalFormModel } from './models';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { DeclineCheckBox } from './decline-checkbox-component';
import { replaceAll } from '../../../../../global/helpers/string-helpers';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { DeclineMedicalDentalView } from './decline-medical-dental-view';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import { gridPageStyles } from '../../../../../global/constants/theme/styles/layout/grid-page-layout';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';

export interface Props {
    wizard: DeclineMedicalDentalWizard;
}
function DeclineMedicalDentalForm(props: Props) {
    const { wizard } = props;
    const errors = useApiResponseError();
    const contentModel = wizard?.getFormContentModel();
    const gpsItem = useGpsType(GpsType.DeclineMedicalDental);
    const year = wizard?.getCurrentYear();
    const groupInfo = wizard?.getGroupInfo();
    const institutionClientNumber = useSelector<TcsmStates, string>(state => state?.institutionState?.institutionClientNumber);
    const buttonClasses = buttonStyles();
    const mapHome = useBasePath(`${ArcRoutes.ArcRoot}`);
    const pageClasses = gridPageStyles();

    return (
        <PageContainer>
            <MainContent fullWidth>
                <div className={pageClasses.gridPageContainer}>
                    <Formik
                        initialValues={wizard?.getFormInitialValue()}
                        onSubmit={
                            (values) => wizard?.onSubmit({ values, institutionId: institutionClientNumber })()
                        }
                        enableReinitialize
                        initialErrors={errors}
                        validationSchema={wizard?.getFormValidationSchema()}
                    >
                        {(formProps: FormikProps<DeclineMedicalDentalFormModel>) => (
                            <>
                                <Form className='grid-fw-content grid-nested'>
                                    <FormWrapper
                                        formikProps={formProps}
                                        onCancel={() => wizard?.onCancel(`${mapHome}${ArcRoutes.InstitutionRoot}${ArcRoutes.PlanSummary}`)}
                                        submitText={contentModel?.declineMedicalDentalSaveText}
                                        cancelText={contentModel?.declineMedicalDentalCancelText}
                                        hideButtons={!contentModel?.allowPlanSelectionPeriod}
                                        buttonFlexEnd={false}
                                    >
                                        <div className='grid-main-content'>
                                            <PageHeader
                                                headerText={contentModel?.declineMedicalDentalPageTitle}
                                                description={contentModel?.declineMedicalDentalPageDescription}
                                            />

                                            <ContentSpacer>
                                                <h2>{contentModel?.groupInfoSectionTitle}</h2>
                                                <p>{contentModel?.groupInfoHelperText}</p>
                                                <p>{groupInfo?.id}-{groupInfo?.name}</p>
                                            </ContentSpacer>
                                        </div>
                                        <Hidden smDown>
                                            <div className='grid-sidebar'>
                                                <SideBarContent>
                                                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                                </SideBarContent>
                                            </div>
                                        </Hidden>
                                        <div className='grid-fw-content'>
                                            <DividerSection removeDivider>
                                                <ContentSpacer>
                                                    <ContentSpacer>
                                                        <div className='rich-text'>
                                                            <h3>{contentModel?.declineMedicalPlanSectionTitle?.replace('{year}', year?.toString())}</h3>
                                                            <p>{replaceAll(contentModel?.availablePeriodText, '{year}', year?.toString())}</p>
                                                            <p className='caption'><strong>{contentModel?.planSectionHelperText}</strong></p>
                                                        </div>
                                                    </ContentSpacer>
                                                    {
                                                        contentModel?.allowPlanSelectionPeriod ?
                                                            <DeclineCheckBox wizard={wizard} plans={wizard?.oePlanOfferings?.medical} formikProps={formProps} propName='medical' /> :
                                                            <DeclineMedicalDentalView wizard={wizard} plans={wizard?.oePlanOfferings?.medical} declinedTitle={contentModel?.declinedMedicalPlanSectionTitle?.replace('{year}', year?.toString())} />
                                                    }
                                                </ContentSpacer>
                                                <ContentSpacer>
                                                    <ContentSpacer>
                                                        <div className='rich-text'>
                                                            <h3>{contentModel?.declineDentalPlanSectionTitle?.replace('{year}', year?.toString())}</h3>
                                                            <p>{replaceAll(contentModel?.availablePeriodText, '{year}', year?.toString())}</p>
                                                            <p className='caption'><strong>{contentModel?.planSectionHelperText}</strong></p>
                                                        </div>
                                                    </ContentSpacer>
                                                    {
                                                        contentModel?.allowPlanSelectionPeriod ?
                                                            <DeclineCheckBox wizard={wizard} plans={wizard?.oePlanOfferings?.dental} formikProps={formProps} propName='dental' /> :
                                                            <DeclineMedicalDentalView wizard={wizard} plans={wizard?.oePlanOfferings?.dental} declinedTitle={contentModel?.declinedDentalPlanSectionTitle?.replace('{year}', year?.toString())} />
                                                    }
                                                </ContentSpacer>
                                            </DividerSection>
                                        </div>
                                    </FormWrapper>
                                </Form>
                            </>
                        )}
                    </Formik>
                    {contentModel?.allowPlanSelectionPeriod && <WysiwygWithReactLink content={contentModel?.buttonHelperText} />} 
                    {
                        !contentModel?.allowPlanSelectionPeriod && 
                            <ButtonWrapper>
                                <button className={buttonClasses.primaryButton} type='button' onClick={() => wizard?.history.push(mapHome)} >{contentModel?.mapHomeButtonText}</button>
                            </ButtonWrapper>
                    }

                </div>
            </MainContent>
        </PageContainer>
    );
}



export default DeclineMedicalDentalForm;